<script lang="ts">
  /**
   * Inline "Tell us you want this" wish list form.
   * Posts to /api/wishlist on swimset.club, which a Cloudflare Worker
   * forwards to the #swim-set-list Slack channel via incoming webhook.
   * Capture context is set per surface so wishes are tagged at source.
   *
   * ABSOLUTE url, not relative (2026-07-13): when the app has fallen back to
   * its BUNDLED copy the page origin is file:// / null, not
   * https://swimset.club, and a relative fetch would resolve against that
   * null origin and silently fail — the owner's wishes went nowhere and
   * nothing on screen said so. text/plain keeps this a CORS "simple"
   * request from that null origin (an application/json content type would
   * force a preflight there); the Worker still returns
   * Access-Control-Allow-Origin on the response either way (see worker.ts).
   * Mirrors saveDiagnostics'/Firehose's "Send the report" POST to /api/diag.
   */
  interface Props {
    context: string;
    prompt?: string;
  }

  let { context, prompt = "What would you like to see here?" }: Props = $props();

  import { haptic } from "../lib/haptics";

  let open = $state(false);
  let text = $state("");
  let sending = $state(false);
  let status: string | null = $state(null);
  let statusKind: "ok" | "error" | null = $state(null);

  function reset() {
    text = "";
    status = null;
    statusKind = null;
  }

  function cancel() {
    open = false;
    reset();
  }

  async function send() {
    const trimmed = text.trim();
    if (trimmed.length === 0 || sending) return;
    sending = true;
    status = null;
    statusKind = null;
    try {
      const res = await fetch("https://swimset.club/api/wishlist", {
        method: "POST",
        headers: { "content-type": "text/plain;charset=UTF-8" },
        body: JSON.stringify({ text: trimmed, context }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };
      if (res.ok && body.ok === true) {
        statusKind = "ok";
        status = "Sent. Thanks for the nudge.";
        text = "";
        haptic("success");
        setTimeout(() => {
          open = false;
          reset();
        }, 1600);
      } else if (body.error === "webhook-not-configured") {
        statusKind = "error";
        status = "Wish list not wired up yet. Check back soon.";
        haptic("error");
      } else {
        statusKind = "error";
        status = `Couldn't send (${body.error ?? `http-${res.status}`}).`;
        haptic("error");
      }
    } catch {
      statusKind = "error";
      status = "Network error. Try again on the pool deck.";
      haptic("error");
    } finally {
      sending = false;
    }
  }
</script>

<div class="wish">
  {#if !open}
    <button type="button" class="opener" onclick={() => { haptic("light"); open = true; }}>
      <span class="plus" aria-hidden="true">+</span>
      <span>Tell us you want this</span>
    </button>
  {:else}
    <div class="form">
      <textarea
        bind:value={text}
        placeholder={prompt}
        rows="3"
        maxlength="2000"
        disabled={sending}
        aria-label="Wish list entry"
      ></textarea>
      <div class="row">
        <button
          type="button"
          class="cancel"
          onclick={cancel}
          disabled={sending}
        >
          Cancel
        </button>
        <button
          type="button"
          class="send"
          onclick={send}
          disabled={sending || text.trim().length === 0}
        >
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
      {#if status}
        <div class="status" class:ok={statusKind === "ok"} class:err={statusKind === "error"} role="status" aria-live="polite">
          {status}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .wish {
    width: 100%;
  }
  .opener {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: transparent;
    border: 1px dashed var(--divider-strong);
    color: var(--text-body);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-pill);
    font: var(--type-caption);
    letter-spacing: 0.04em;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background-color var(--dur-micro) var(--ease-swim),
                border-color var(--dur-micro) var(--ease-swim),
                color var(--dur-micro) var(--ease-swim);
  }
  .opener:hover {
    background: var(--bg-surface);
    border-color: var(--accent-primary);
    color: var(--text-primary);
  }
  .opener:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }
  .plus {
    color: var(--accent-primary);
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3);
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
  }
  textarea {
    width: 100%;
    background: var(--bg-input);
    color: var(--text-primary);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    padding: var(--space-2) var(--space-3);
    font: var(--type-body-sm);
    resize: vertical;
    min-height: 72px;
    -webkit-appearance: none;
    appearance: none;
  }
  textarea:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 1px;
    border-color: var(--accent-primary);
  }

  .row {
    display: flex;
    gap: var(--space-2);
    justify-content: flex-end;
  }
  .row button {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-button);
    font: var(--type-caption);
    letter-spacing: 0.04em;
    cursor: pointer;
    border: 1px solid var(--divider);
    transition: background-color var(--dur-micro) var(--ease-swim),
                opacity var(--dur-micro) var(--ease-swim);
    -webkit-tap-highlight-color: transparent;
  }
  .cancel {
    background: transparent;
    color: var(--text-muted);
  }
  .cancel:hover:not(:disabled) {
    background: var(--bg-raised);
    color: var(--text-primary);
  }
  .send {
    background: var(--accent-primary);
    color: var(--bg-canvas);
    border-color: var(--accent-primary);
    font-weight: 600;
  }
  .send:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .status {
    font: var(--type-caption);
    letter-spacing: 0.04em;
    padding: var(--space-2);
    border-radius: var(--radius-button);
    text-align: center;
  }
  .status.ok {
    color: var(--accent-primary);
    background: var(--bg-raised);
  }
  .status.err {
    color: var(--accent-error);
    background: var(--bg-raised);
  }
</style>
